<script setup>
import { ref, computed, onMounted } from 'vue'
import userInventoryCalls from '@core/api/modules/UserInventoryCalls'
import DashboardHeader from '../../components/dashboard/DashboardHeader.vue'
import DashboardTabs from '../../components/dashboard/DashboardTabs.vue'
import { useAuthStore } from '@core/stores/AuthStore'
import API from '@core/api/API'

definePage({
  meta: {
    layout: 'default',
    authenticatedOnly: true,
  },
})

const { t } = useI18n()

const authStore = useAuthStore()

// Reactive data
const cycles = ref([])
const currentCycleIndex = ref(0)
const isLoadingWeatherForecast = ref(false)
const weatherForecastError = ref(null)
const weatherForecast = ref(null)

// Computed properties
const currentCycle = computed(() => {
  return cycles.value.length > 0 ? cycles.value[currentCycleIndex.value] : null
})

// Methods
const nextCycle = () => {
  if (cycles.value.length > 1) {
    currentCycleIndex.value = (currentCycleIndex.value + 1) % cycles.value.length
  }
}

const sortCyclesByDate = cyclesArray => {
  return cyclesArray.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
}

const fetchUserGrowCycles = async () => {
  try {
    const fetchedCycles = await userInventoryCalls.getUserGrowCycles()

    cycles.value = sortCyclesByDate(fetchedCycles)
  } catch (error) {
    console.error('Error fetching grow cycles:', error)
  }
}

const fetchWeatherForecasts = async () => {
  isLoadingWeatherForecast.value = true

  try {
    const res = await API.UserCalls.getLocationWeatherForecast(authStore.getToken)

    if (res) {
      weatherForecast.value = res
    }
  } catch (error) {
    console.error(`Failed to refresh weather forecast for the current location: `, error)
    weatherForecastError.value = t('Unable to fetch weather information')
  } finally {
    isLoadingWeatherForecast.value = false
  }
}

// Lifecycle
onMounted(async () => {
  if (!authStore.isAuthenticated) {
    return
  }

  if (!authStore.getUser) {
    await authStore.refreshAuthUser()
  }

  await fetchWeatherForecasts()
  await fetchUserGrowCycles()
})
</script>

<template>
  <div>
    <!-- Header with Title and Next Button -->
    <DashboardHeader
      :cycles="cycles"
      :current-cycle="currentCycle"
      @next-cycle="nextCycle"
    />

    <!-- Tabs Section -->
    <DashboardTabs
      :current-cycle="currentCycle"
      :weather-forecast="{
        isLoading: isLoadingWeatherForecast,
        error: weatherForecastError,
        ...weatherForecast
      }"
    />
  </div>
</template>

<style lang="scss">
.gap-2 {
  gap: 8px;
}
</style>
